/**
 * Custom Knowledge libraries (named groups of books) scoped to org + knowledge base.
 *
 * No server libraries API exists yet (articles + file index only). Storage matches
 * custom books so One and Agents stay in sync via shared localStorage keys.
 */

import { DEFAULT_KNOWLEDGE_BASE_ID } from "@/lib/knowledgeBaseRegistry";
import {
  normalizeBookCollectionKey,
  type KnowledgeCustomBook,
} from "@/lib/knowledgeBookRegistry";
import { OTHER_LIBRARY_ID, SUITE_LIBRARY_ID } from "@/lib/knowledgeModel";

export type KnowledgeCustomLibrary = {
  id: string;
  title: string;
  description: string;
  createdAt: string;
};

export type KnowledgeLibrarySource = "registry" | "inferred";

export type ResolvedKnowledgeLibrary = KnowledgeCustomLibrary & {
  source: KnowledgeLibrarySource;
};

import { publishKnowledgeRegistryForOrg } from "@/lib/knowledgeRegistryCrossSync";

const STORAGE_PREFIX = "atw-one.knowledge.customLibraries";
export const KNOWLEDGE_CUSTOM_LIBRARIES_CHANGED = "atw-one:knowledge-custom-libraries-changed";

const RESERVED_LIBRARY_IDS = new Set<string>([SUITE_LIBRARY_ID, OTHER_LIBRARY_ID]);

function storageKey(orgId: string, kbId: string): string {
  return `${STORAGE_PREFIX}.${orgId || "default"}.${kbId || DEFAULT_KNOWLEDGE_BASE_ID}`;
}

function isCustomLibrary(value: unknown): value is KnowledgeCustomLibrary {
  if (!value || typeof value !== "object") return false;
  const row = value as KnowledgeCustomLibrary;
  return (
    typeof row.id === "string" &&
    row.id.trim().length > 0 &&
    typeof row.title === "string" &&
    row.title.trim().length > 0 &&
    typeof row.description === "string" &&
    typeof row.createdAt === "string"
  );
}

export function suggestLibraryId(title: string): string {
  const slug = title
    .trim()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
  return slug || "library";
}

export function normalizeLibraryId(raw: string): string {
  return raw.trim().replace(/\s+/g, "-").slice(0, 120);
}

/** Display title when a library exists only as a slug on books or collection keys. */
export function humanizeLibraryId(id: string): string {
  const normalized = normalizeLibraryId(id);
  if (!normalized) return "Library";
  return normalized
    .split("-")
    .filter(Boolean)
    .map((word, index) =>
      index === 0
        ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        : word.toLowerCase(),
    )
    .join(" ");
}

export function isIntentionalLibrarySlug(raw: string): boolean {
  const id = normalizeLibraryId(raw);
  if (!id || RESERVED_LIBRARY_IDS.has(id)) return false;
  return /^[a-z0-9][a-z0-9-]*$/i.test(id);
}

function inferredLibraryIdsFromBooks(customBooks: KnowledgeCustomBook[]): string[] {
  const ids = new Set<string>();
  for (const book of customBooks) {
    const id = normalizeLibraryId(book.libraryId || "");
    if (!id || RESERVED_LIBRARY_IDS.has(id)) continue;
    ids.add(id);
  }
  return [...ids];
}

function bookKeysMatchLibraryId(libraryId: string, bookKeys: Iterable<string>): boolean {
  const target = normalizeLibraryId(libraryId);
  if (!target) return false;
  for (const raw of bookKeys) {
    const key = normalizeBookCollectionKey(raw);
    if (!key) continue;
    if (key === target) return true;
    if (key.startsWith(`${target}/`)) return true;
  }
  return false;
}

/** Registry row, or a virtual library inferred from book assignments or matching collection keys. */
export function resolveKnowledgeLibrary(
  orgId: string | null | undefined,
  libraryId: string,
  kbId: string | null | undefined,
  customBooks: KnowledgeCustomBook[],
  bookKeys: Iterable<string>,
): ResolvedKnowledgeLibrary | null {
  const id = normalizeLibraryId(libraryId);
  if (!id || RESERVED_LIBRARY_IDS.has(id)) return null;

  const custom = getCustomLibrary(orgId, id, kbId);
  if (custom) return { ...custom, source: "registry" };

  const hasAssignedBooks = customBooks.some(
    (book) => normalizeLibraryId(book.libraryId || "") === id,
  );
  if (hasAssignedBooks || bookKeysMatchLibraryId(id, bookKeys)) {
    return {
      id,
      title: humanizeLibraryId(id),
      description: "",
      createdAt: "",
      source: "inferred",
    };
  }

  return null;
}

/** Libraries list: saved rows plus inferred ids from book assignments (not every book key). */
export function listKnowledgeLibraries(
  orgId: string | null | undefined,
  kbId: string | null | undefined,
  customBooks: KnowledgeCustomBook[],
): ResolvedKnowledgeLibrary[] {
  const byId = new Map<string, ResolvedKnowledgeLibrary>();
  for (const library of listCustomLibraries(orgId, kbId)) {
    byId.set(library.id, { ...library, source: "registry" });
  }
  for (const id of inferredLibraryIdsFromBooks(customBooks)) {
    if (byId.has(id)) continue;
    byId.set(id, {
      id,
      title: humanizeLibraryId(id),
      description: "",
      createdAt: "",
      source: "inferred",
    });
  }
  return [...byId.values()].sort((a, b) => a.title.localeCompare(b.title));
}

function parseLibraryList(raw: string | null): KnowledgeCustomLibrary[] {
  if (!raw) return [];
  const parsed = JSON.parse(raw) as unknown;
  if (!Array.isArray(parsed)) return [];
  const byId = new Map<string, KnowledgeCustomLibrary>();
  for (const item of parsed) {
    if (!isCustomLibrary(item)) continue;
    const id = normalizeLibraryId(item.id);
    if (!id || RESERVED_LIBRARY_IDS.has(id)) continue;
    byId.set(id, {
      id,
      title: item.title.trim(),
      description: item.description.trim(),
      createdAt: item.createdAt,
    });
  }
  return [...byId.values()].sort((a, b) => a.title.localeCompare(b.title));
}

export function listCustomLibraries(
  orgId: string | null | undefined,
  kbId: string | null | undefined = DEFAULT_KNOWLEDGE_BASE_ID,
): KnowledgeCustomLibrary[] {
  if (typeof window === "undefined") return [];
  const baseId = (kbId || "").trim();
  if (!baseId) return [];
  try {
    return parseLibraryList(window.localStorage.getItem(storageKey(orgId || "default", baseId)));
  } catch {
    return [];
  }
}

function writeCustomLibraries(
  orgId: string | null | undefined,
  kbId: string | null | undefined,
  libraries: KnowledgeCustomLibrary[],
): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      storageKey(orgId || "default", kbId || DEFAULT_KNOWLEDGE_BASE_ID),
      JSON.stringify(libraries),
    );
    window.dispatchEvent(new CustomEvent(KNOWLEDGE_CUSTOM_LIBRARIES_CHANGED));
    publishKnowledgeRegistryForOrg(orgId);
  } catch {
    /* ignore quota / private mode */
  }
}

export function getCustomLibrary(
  orgId: string | null | undefined,
  libraryId: string,
  kbId: string | null | undefined = DEFAULT_KNOWLEDGE_BASE_ID,
): KnowledgeCustomLibrary | null {
  const id = normalizeLibraryId(libraryId);
  return listCustomLibraries(orgId, kbId).find((row) => row.id === id) || null;
}

export function upsertCustomLibrary(
  orgId: string | null | undefined,
  input: { title: string; description?: string; id?: string },
  kbId: string | null | undefined = DEFAULT_KNOWLEDGE_BASE_ID,
): { ok: true; library: KnowledgeCustomLibrary } | { ok: false; error: string } {
  const title = input.title.trim();
  if (!title) return { ok: false, error: "Title is required." };
  if (!(kbId || "").trim()) {
    return { ok: false, error: "Select or create a knowledge base first." };
  }

  const id = normalizeLibraryId(input.id?.trim() ? input.id : suggestLibraryId(title));
  if (!id) return { ok: false, error: "Enter a valid id or title." };
  if (RESERVED_LIBRARY_IDS.has(id)) {
    return { ok: false, error: "That id is reserved. Choose a different title or id." };
  }
  if (id.startsWith("kb:")) {
    return { ok: false, error: "Ids starting with kb: are reserved for knowledge base membership." };
  }

  const existing = listCustomLibraries(orgId, kbId);
  const previous = existing.find((row) => row.id === id);
  const library: KnowledgeCustomLibrary = {
    id,
    title,
    description: (input.description || "").trim(),
    createdAt: previous?.createdAt || new Date().toISOString(),
  };
  const next = [...existing.filter((row) => row.id !== id), library].sort((a, b) =>
    a.title.localeCompare(b.title),
  );
  writeCustomLibraries(orgId, kbId, next);
  return { ok: true, library };
}

/** Remove one saved library from the local cache (after a successful API delete). */
export function removeCustomLibrary(
  orgId: string | null | undefined,
  libraryId: string,
  kbId: string | null | undefined = DEFAULT_KNOWLEDGE_BASE_ID,
): void {
  const id = normalizeLibraryId(libraryId);
  if (!id) return;
  const next = listCustomLibraries(orgId, kbId).filter((row) => row.id !== id);
  writeCustomLibraries(orgId, kbId, next);
}

/** Drop the client-only custom library list for one knowledge base. */
export function clearCustomLibrariesForKnowledgeBase(
  orgId: string | null | undefined,
  kbId: string | null | undefined,
): void {
  if (typeof window === "undefined") return;
  const baseId = (kbId || "").trim();
  if (!baseId) return;
  try {
    window.localStorage.removeItem(storageKey(orgId || "default", baseId));
    window.dispatchEvent(new CustomEvent(KNOWLEDGE_CUSTOM_LIBRARIES_CHANGED));
    publishKnowledgeRegistryForOrg(orgId);
  } catch {
    /* ignore */
  }
}

export function resolveBookLibraryId(
  collectionKey: string,
  customBooks: KnowledgeCustomBook[],
): string | null {
  const key = collectionKey.trim();
  const match = customBooks.find((book) => book.collectionKey === key);
  const libId = match?.libraryId?.trim();
  return libId || null;
}

export function collectionKeysForLibrary(
  libraryId: string,
  bookKeys: Iterable<string>,
  customBooks: KnowledgeCustomBook[],
): string[] {
  const target = normalizeLibraryId(libraryId);
  const keys = [...bookKeys];
  if (target === OTHER_LIBRARY_ID) {
    return keys.filter((key) => !resolveBookLibraryId(key, customBooks)).sort();
  }
  const assigned = keys.filter((key) => resolveBookLibraryId(key, customBooks) === target);
  if (assigned.length > 0) return assigned.sort();

  return keys
    .filter((key) => {
      const normalized = normalizeBookCollectionKey(key);
      return normalized === target || normalized.startsWith(`${target}/`);
    })
    .sort();
}
